<template>
  <div class="musubi-page musubi-page--leads">
    <header class="musubi-page__head">
      <h1>Prospects</h1>
      <p class="text-muted">
        Firms surfaced by the crawler + enrichment, scored against fit + intent (hottest first).
        A prospect becomes a <strong>Lead</strong> once it shows intent — a buying signal at discovery
        (warm) or engagement after outreach. Triage each: <strong>Qualify</strong>, <strong>Reject</strong>,
        or <strong>Route</strong> to a drip (nurture) or direct outreach.
      </p>
    </header>

    <!-- Enterprise data-view gate (LHS-rework Screen C). The PAGE route perm is
         unchanged (Ops::ACQUIRE) — the gate is component-level: a non-entitled
         tenant reaching /acquire/prospects directly gets an honest locked panel
         instead of the raw corpus browser. Per-pipeline "View matches" (a
         pipeline-scoped surface) stays for everyone; this only gates the
         standing corpus browser, which is an Enterprise / operator data view.
         `gateLocked` is null while /me/usage loads (renders nothing yet). -->
    <div v-if="gateLocked" class="leads-ent-lock">
      <div class="leads-ent-lock__ic"><CygnusIcon name="award" /></div>
      <div class="leads-ent-lock__title">The Prospects browser is an Enterprise data view</div>
      <p class="leads-ent-lock__sub">
        Browsing and filtering the raw prospect corpus is an Enterprise feature. On your
        current plan you work from <strong>per-pipeline matches</strong> — open a pipeline and
        choose <strong>View matches</strong> to see exactly who it surfaced. Enterprise opens up
        the whole corpus, plus taxonomies and shared saved views.
      </p>
      <div class="leads-ent-lock__actions">
        <router-link to="/" class="btn btn-outline-secondary btn-sm">
          <CygnusIcon name="diagram" class="me-1" /> Back to pipelines
        </router-link>
        <router-link to="/admin/usage-plans" class="leads-ent-lock__compare">
          Compare plans <CygnusIcon name="arrow-right" />
        </router-link>
      </div>
    </div>

    <template v-else-if="gateLocked === false">
    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>
    <p v-if="message" class="alert alert-success py-2 small">{{ message }}</p>

    <!-- Deep-linked from a pipeline card's "View matches" (?pipeline=). Honest about
         scope: only the FIRST audience source's stage/temperature/leads-only/sector/
         company-status axes are pre-applied to the controls below — a multi-source
         pipeline's OR-of-sources can't be represented in this AND-only browser, and
         the framework/region/staff/score axes aren't pre-applied (see
         applyPipelineParam). Use "Export matches" on the pipeline card for the exact
         matching set. -->
    <div v-if="pipelineId" class="pipeline-chip">
      <CygnusIcon name="filter" class="me-1" />
      Filtered to pipeline: <strong>{{ pipelineName }}</strong> <span class="text-muted">(first audience rule)</span>
      <a href="/acquire/prospects" class="pipeline-chip__clear">Clear</a>
    </div>

    <section class="lead-kpis">
      <MusubiKpiCard label="Prospects" :value="kpis.total" icon="users"
        :delta="filtered ? 'matching filters' : 'all'" delta-dir="neutral" />
      <MusubiKpiCard label="Leads" :value="kpis.leads" icon="flame"
        :delta="kpis.total ? kpis.leadPct + '% show intent' : ''" :delta-dir="kpis.leads ? 'up' : 'neutral'" />
      <MusubiKpiCard label="Enriched" :value="kpis.enriched" icon="bolt"
        :delta="kpis.enrichedPct + '% coverage'" :delta-dir="kpis.enrichedPct >= 80 ? 'up' : 'neutral'" />
      <MusubiKpiCard label="Pain points" :value="kpis.pain" icon="pin"
        :delta="kpis.pain ? 'outreach hooks' : 'none'" :delta-dir="kpis.pain ? 'up' : 'neutral'" />
      <MusubiKpiCard label="Ready to promote" :value="kpis.promotable" icon="external-link"
        :delta="kpis.promotable ? 'engaged' : 'none'" :delta-dir="kpis.promotable ? 'up' : 'neutral'" />
    </section>

    <section class="actions-bar">
      <select v-model="stage" class="form-select form-select-sm" style="width:auto" @change="applyTopFilter">
        <option value="">All stages</option>
        <option value="new">New</option>
        <option value="qualifying">Qualifying</option>
        <option value="qualified">Qualified</option>
        <option value="promoted">Promoted</option>
        <option value="rejected">Rejected</option>
      </select>
      <select v-model="temperature" class="form-select form-select-sm" style="width:auto" @change="applyTopFilter">
        <option value="">Warm + cold</option>
        <option value="warm">🔥 Warm only</option>
        <option value="cold">❄️ Cold only</option>
      </select>
      <select v-model="pain" class="form-select form-select-sm" style="width:auto" @change="load" title="Filter to firms with a specific outreach pain point">
        <option value="">Any hooks</option>
        <option value="any">⚑ Has a pain point</option>
        <option value="no_website">No website</option>
        <option value="ssl">Broken SSL</option>
        <option value="platform">Outdated / EOL platform</option>
        <option value="stale">Stale / aging site</option>
        <option value="parked">Parked / expired site</option>
        <option value="dissolved">Company dissolved</option>
        <option value="hiring">Currently hiring</option>
      </select>
      <MusubiSectorSelect
        v-model="sectorTokens"
        compact
        placeholder="Any industry"
        class="leads-sector-filter"
        @update:modelValue="applyTopFilter" />
      <select v-model="companyStatus" class="form-select form-select-sm" style="width:auto" @change="applyTopFilter"
        title="Filter by Companies House status">
        <option value="">Any status</option>
        <option value="active">Active</option>
        <option value="dissolved">Dissolved</option>
        <option value="liquidation">Liquidation</option>
        <option value="administration">Administration</option>
        <option value="receivership">Receivership</option>
      </select>
      <!-- Registration status — multi-select (registered / unregistered / public
           sector / not-yet-checked), same semantics + labels as the Direct Mail
           audience filter's chip picker. Empty selection = no constraint. -->
      <div class="reg-status-filter">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="regStatusOpen = !regStatusOpen"
          title="Filter by Companies House registration status">
          <CygnusIcon name="building" class="me-1" />{{ regStatusLabel }}
        </button>
        <div v-if="regStatusOpen" class="col-chooser__panel">
          <label v-for="s in registrationStatusOptions" :key="s.value" class="col-chooser__row">
            <input type="checkbox" :value="s.value" v-model="registrationStatuses" @change="applyTopFilter">
            {{ s.label }}
          </label>
        </div>
      </div>
      <label class="promotable-toggle small text-muted">
        <input type="checkbox" v-model="leadsOnly" @change="applyTopFilter"> Leads only
      </label>
      <label class="promotable-toggle small text-muted">
        <input type="checkbox" v-model="promotable" @change="applyTopFilter"> Ready to promote
      </label>
      <button class="btn btn-link" :disabled="loading" @click="refresh">
        <CygnusIcon name="refresh" /> Refresh
      </button>

      <!-- Column chooser (F-PT-02) — the corpus browser packs ~19 possible
           columns; showing them all squeezes every header to 1-2 letters
           under fitColumns layout. Default to a sane triage set (name, stage,
           temp, score, company status, region, source, type); the rest stay
           available here, one click away. Preference persists per-browser. -->
      <div class="col-chooser">
        <button class="btn btn-outline-secondary btn-sm" @click="columnChooserOpen = !columnChooserOpen"
          title="Choose which columns are shown">
          <CygnusIcon name="table" class="me-1" />Columns
        </button>
        <div v-if="columnChooserOpen" class="col-chooser__panel">
          <div class="col-chooser__head">
            <span>Show columns</span>
            <button class="btn btn-link btn-sm p-0" @click="resetColumns">Reset to default</button>
          </div>
          <label v-for="c in toggleableColumns" :key="c.key" class="col-chooser__row">
            <input type="checkbox" :checked="visibleCols.includes(c.key)" @change="toggleColumn(c.key)">
            {{ c.plainLabel }}
          </label>
        </div>
      </div>

      <span class="actions-spacer"></span>
      <select v-model="bulkMethod" class="form-select form-select-sm" style="width:auto" title="Run an enrichment method across the leads matching the current stage/temperature filter">
        <option value="">Enrich method…</option>
        <option v-for="m in methods" :key="m.name" :value="m.name">{{ m.label }}{{ m.enabled ? '' : ' (off)' }}</option>
      </select>
      <button class="btn btn-outline-primary btn-sm" :disabled="!bulkMethod || bulkBusy" @click="runBulk"
        title="Queues the chosen method (override) for the filtered leads — runs in the background">
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="bulkBusy" />Run on filtered
      </button>
    </section>

    <!-- Selection helpers — operate on the rows passing the active filters -->
    <section class="select-bar">
      <button class="btn btn-outline-secondary btn-sm" :disabled="!filteredCount || idsBusy" @click="selectAllMatching">
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="idsBusy" />
        <CygnusIcon name="check" class="me-1" v-else />Select all {{ filteredCount.toLocaleString() }} matching
      </button>
      <button v-if="selectedCount" class="btn btn-link btn-sm" @click="clearSelection">Clear selection</button>
      <span class="actions-bar__msg">Filters above + column filters define the set; "select all" spans every page.</span>
    </section>

    <!-- Bulk: add selected prospects to a drip flow -->
    <section v-if="selectedCount" class="bulk-bar">
      <span class="bulk-bar__count">
        <strong>{{ selectedCount.toLocaleString() }}</strong> selected<template v-if="allMatching"> (all matching)</template>
      </span>
      <MusubiEntitySelect
        v-model="bulkFlowId"
        endpoint="/api/v1/admin/drip-flows"
        :option-label="f => f.name"
        placeholder="Add to drip flow…" style="width:auto; min-width:200px" />
      <button class="btn btn-primary btn-sm" :disabled="!bulkFlowId || bulkDripBusy" @click="addSelectedToDrip">
        <CygnusIcon name="spinner" :spin="true" class="me-1" v-if="bulkDripBusy" />Add {{ selectedCount.toLocaleString() }} to flow
      </button>
      <span v-if="bulkDripMsg" class="actions-bar__msg">{{ bulkDripMsg }}</span>
    </section>

    <!-- F-PT-01: explicit "open prospect" affordance (name link + chevron cell).
         @click.capture runs BEFORE Tabulator's own row-click handler (which
         toggles the checkbox on any row click) because the capture phase
         walks top-down from this wrapper into the table's DOM ahead of the
         bubble-phase listener Tabulator attaches to the row. stopPropagation
         here means a click on .lead-open never reaches Tabulator's selection
         logic — opening navigates, nothing else selects. Selection stays
         exactly as before: only the leading checkbox / anywhere-else-on-row
         click. -->
    <div class="leads-table-wrap" @click.capture="onOpenCapture">
      <MusubiTabulator
        ref="leadsTable"
        :columns="columns"
        storage-key="admin.leads.v8"
        :initial-sort="{ column: 'score', dir: 'desc' }"
        :page-size="25"
        remote
        :fetcher="fetcher"
        selectable
        @selection-change="onSelection"
        @params-change="onParamsChange"
        @filtered-count="filteredCount = $event"
      />
    </div>

    <!-- Route modal (own template → @click binds; Tabulator-cell buttons use delegation) -->
    <div v-if="routing" class="leads-modal-overlay" @click.self="closeRoute">
      <div class="leads-modal">
        <div class="leads-modal__head">
          <h6 class="mb-0">Route lead: {{ routing.name }} <span class="text-muted small">({{ routing.platform }})</span></h6>
          <button class="btn btn-link btn-sm" @click="closeRoute">Close</button>
        </div>

        <div class="leads-modal__body">
          <div class="mb-3">
            <div class="form-label small text-muted">Route to</div>
            <label class="me-3"><input type="radio" value="drip" v-model="form.route"> Drip (nurture)</label>
            <label><input type="radio" value="direct" v-model="form.route"> Direct outreach</label>
          </div>

          <template v-if="form.route === 'drip'">
            <div class="mb-2">
              <label class="form-label small text-muted">Email *</label>
              <input v-model="form.email" type="email" class="form-control form-control-sm" placeholder="lead@example.com">
              <div class="form-text small">Required — crawler leads have no email until enriched (P12).</div>
            </div>
            <div class="row g-2">
              <div class="col">
                <label class="form-label small text-muted">List</label>
                <select v-model="form.list_id" class="form-select form-select-sm">
                  <option :value="null">— none —</option>
                  <option v-for="l in lists" :key="l.id" :value="l.id">{{ l.name || ('List') }} (#{{ l.id }})</option>
                </select>
              </div>
              <div class="col">
                <label class="form-label small text-muted">Drip flow</label>
                <select v-model="form.drip_flow_id" class="form-select form-select-sm">
                  <option :value="null">— none —</option>
                  <option v-for="f in flows" :key="f.id" :value="f.id">{{ f.name || ('Flow') }} (#{{ f.id }})</option>
                </select>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="mb-2">
              <label class="form-label small text-muted">Assign to (user ID)</label>
              <input v-model.number="form.assigned_to" type="number" class="form-control form-control-sm" placeholder="defaults to you">
            </div>
          </template>
        </div>

        <div class="leads-modal__foot">
          <button class="btn btn-primary btn-sm" :disabled="busy" @click="submitRoute">
            <CygnusIcon name="spinner" :spin="true" v-if="busy" /><span v-else>Route</span>
          </button>
          <button class="btn btn-link btn-sm" @click="closeRoute">Cancel</button>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import MusubiTabulator from "@root/components/MusubiTabulator.vue";
import MusubiEntitySelect from "@root/components/MusubiEntitySelect.vue";
import MusubiSectorSelect from "@root/components/MusubiSectorSelect.vue";
import MusubiKpiCard from "@root/components/ui/MusubiKpiCard.vue";
import { esc, safeUrl } from "./util/cellHtml";
import cygnusSpriteHtml from "@root/services/icons/cygnusSpriteHtml";

// Stage → pill tone, matching MusubiStatusPill's tone map for the same keywords.
const STAGE_TONE = { new: 'neutral', qualifying: 'warning', qualified: 'success', promoted: 'accent', rejected: 'danger' };
// Budget band → pill tone (lightest → strongest signal).
const BUDGET_TONE = { minimal: 'neutral', modest: 'info', moderate: 'accent', substantial: 'success' };
// Company-status → pill tone, mirroring the top-bar filter's own options.
const CSTATUS_TONE = { active: 'success', dissolved: 'danger', liquidation: 'danger', administration: 'warning', receivership: 'warning' };

// F-PT-02: default visible columns — a sane triage set (the corpus browser has
// ~19 possible columns; showing them all under fitColumns squeezes every
// header to 1-2 letters). Everything else is one click away in the column
// chooser. NOTE: "sector" was part of the requested default set but the list
// endpoint (LeadReadService::LIST_FIELDS) doesn't return it per-row today —
// only the single-record detail fetch does — so it can't be a table column
// without a backend change; "Type" (lead vs prospect) fills that slot instead.
const DEFAULT_VISIBLE_COLS = ['is_lead', 'temperature', 'score', 'stage', 'name', 'company_status', 'region', 'platform'];
const VISIBLE_COLS_KEY = 'musubi.leads.visibleCols.v1';

export default {
  name: 'AdminLeads',
  components: { MusubiTabulator, MusubiEntitySelect, MusubiSectorSelect, MusubiKpiCard },
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      // Enterprise data-view gate: null = /me/usage not yet read (render nothing),
      // true = locked (show the Enterprise panel), false = entitled (show browser).
      gateLocked: null,
      pageRows: [],
      loading: false,
      busy: false,
      error: null,
      message: '',
      stage: '',
      temperature: '',
      promotable: false,
      leadsOnly: false,
      pain: '',
      // Firmographic filters (sent in the remote-filter shape alongside the
      // Tabulator column filters).
      sectorTokens: [],
      companyStatus: '',
      // Registration status — multi-select; 'unchecked' is a pseudo-status
      // (registration_status IS NULL), never a stored value — see
      // AudienceFilters (Direct Mail) whose split-clause logic this filter reuses.
      registrationStatuses: [],
      regStatusOpen: false,
      registrationStatusOptions: [
        { value: 'registered', label: 'Registered' },
        { value: 'public_sector', label: 'Public sector' },
        { value: 'unregistered', label: 'Unregistered' },
        { value: 'unchecked', label: 'Not yet checked' },
      ],
      methods: [],
      bulkMethod: '',
      bulkBusy: false,
      // Server-side counts for the matching set (KPI cards).
      stats: { total: 0, leads: 0, enriched: 0, pain: 0, promotable: 0 },
      promoteThreshold: 10,
      // Selection: per-page ticks (selected) OR a cross-page "all matching" set.
      selected: [],
      allMatching: false,
      allMatchingIds: [],
      idsBusy: false,
      filteredCount: 0,
      colFiltersActive: false,
      lastSig: null,
      // Distinct values feeding the list header-filter dropdowns (remote mode
      // can't derive them from a single loaded page).
      distinct: { platform: [], website_framework: [], region: [] },
      bulkFlowId: null,
      bulkDripBusy: false,
      bulkDripMsg: '',
      lists: [],
      flows: [],
      routing: null,
      form: { route: 'drip', email: '', list_id: null, drip_flow_id: null, assigned_to: null },
      // ?pipeline={id} deep-link (see applyPipelineParam) — set once the
      // pipeline's name resolves, drives the "Filtered to pipeline" chip.
      pipelineId: null,
      pipelineName: '',
      // F-PT-02: column visibility (chooser) — see DEFAULT_VISIBLE_COLS.
      columnChooserOpen: false,
      visibleCols: this.loadVisibleCols(),
    };
  },
  computed: {
    // {value:label} map for a list header filter — leading "All" clears it.
    // Declared here so the columns computed can reuse it for every dropdown.
    listVals() {
      return (arr) => ({ '': 'All', ...Object.fromEntries((arr || []).map((v) => [v, v])) });
    },
    filtered() {
      return !!(this.stage || this.temperature || this.pain || this.leadsOnly || this.promotable
        || this.colFiltersActive || this.sectorTokens.length || this.companyStatus
        || this.registrationStatuses.length);
    },
    /** Button label for the registration-status dropdown — count once >1 selected. */
    regStatusLabel() {
      const n = this.registrationStatuses.length;
      if (!n) return 'Any registration';
      if (n === 1) {
        const opt = this.registrationStatusOptions.find((o) => o.value === this.registrationStatuses[0]);
        return opt ? opt.label : 'Registration';
      }
      return `Registration (${n})`;
    },
    selectedCount() {
      return this.allMatching ? this.allMatchingIds.length : this.selected.length;
    },
    bulkIds() {
      return this.allMatching ? this.allMatchingIds : this.selected.map((r) => r.id);
    },
    kpis() {
      const s = this.stats;
      const pct = (n) => (s.total ? Math.round((n / s.total) * 100) : 0);
      return {
        total: s.total,
        leads: s.leads,
        leadPct: pct(s.leads),
        enriched: s.enriched,
        enrichedPct: pct(s.enriched),
        pain: s.pain,
        promotable: s.promotable,
      };
    },
    /**
     * F-PT-02: the full column catalogue. `togglable: false` columns (the
     * chevron-open cell and Actions) are always shown; everything else is
     * gated by `visibleCols` (the column chooser). Abbreviated headers
     * (Score/Fit/Engmt/Temp/Match) carry a `title` tooltip in the label HTML
     * itself — Tabulator writes `def.title` straight into innerHTML, so a
     * wrapped <span title="…"> gives a real hover tooltip without needing any
     * change to MusubiTabulator's column-def passthrough.
     */
    allDataColumns() {
      return [
        {
          key: '_open', label: '', plainLabel: '', togglable: false,
          width: 34, hozAlign: 'center', sorter: false, headerFilter: false,
          format: (v, row) => `<a href="/acquire/prospects/${esc(row.id)}" class="lead-open lead-open--chevron" data-id="${esc(row.id)}" title="Open ${esc(row.name || 'prospect')}">${cygnusSpriteHtml('chevron-right')}</a>`,
        },
        { key: 'is_lead', label: 'Type', plainLabel: 'Type (lead / prospect)', width: 90, hozAlign: 'center', sorter: false, headerFilter: false,
          format: (v, row) => row.is_lead
            ? '<span class="tpill tpill--success" title="Shows intent — buying signal or engagement">Lead</span>'
            : '<span class="text-muted small" title="Discovered, no intent signal yet">prospect</span>' },
        { key: 'temperature', label: '<span title="Warm = a buying signal at discovery. Cold = none yet.">Temp</span>', plainLabel: 'Temperature', width: 90, hozAlign: 'center',
          headerFilter: 'list', headerFilterParams: { values: { '': 'All', warm: '🔥 Warm', cold: '❄️ Cold' } },
          format: (v) => v === 'cold' ? '<span class="tpill tpill--cold">❄️ cold</span>' : '<span class="tpill tpill--warm">🔥 warm</span>' },
        { key: 'score',     label: '<span title="Overall lead score — fit + engagement + discovery signals combined.">Score</span>', plainLabel: 'Score (overall)', width: 84,  sorter: 'number',
          headerFilter: 'number', headerFilterFunc: '>=', headerFilterPlaceholder: '≥' },
        { key: 'fit_score', label: '<span title="Fit score — how well this prospect matches your ideal customer profile.">Fit</span>', plainLabel: 'Fit score',    width: 76,  sorter: 'number',
          headerFilter: 'number', headerFilterFunc: '>=', headerFilterPlaceholder: '≥',
          format: (v) => (v ? `<strong>${esc(v)}</strong>` : '—') },
        { key: 'engagement_score', label: '<span title="Engagement score — opens, clicks and replies after outreach.">Engmt</span>', plainLabel: 'Engagement score', width: 84,
          headerFilter: 'number', headerFilterFunc: '>=', headerFilterPlaceholder: '≥', sorter: 'number',
          format: (v, row) => (v ? (row.promotable ? `<strong class="text-success">${esc(v)}</strong>` : esc(v)) : '—') },
        { key: 'stage',     label: 'Stage',  plainLabel: 'Stage', width: 120,
          headerFilter: 'list', headerFilterParams: { values: { '': 'All', new: 'new', qualifying: 'qualifying', qualified: 'qualified', promoted: 'promoted', rejected: 'rejected' }, clearable: true },
          format: (v) => v ? `<span class="tpill tpill--${STAGE_TONE[v] || 'neutral'}">${esc(v)}</span>` : '—' },
        { key: 'name',      label: 'Name',   plainLabel: 'Name', width: 200, headerFilterPlaceholder: 'Search…',
          // F-PT-01: the name is now a real open affordance (router-linked via
          // the .lead-open capture handler below), not plain text.
          format: (v, row) => {
            let nm = `<a href="/acquire/prospects/${esc(row.id)}" class="lead-open lead-open--name" data-id="${esc(row.id)}">${esc(v || '—')}</a>`;
            if (row.registration_status === 'unregistered') {
              nm += ` <span class="text-danger small" title="Not in Companies House — possible sole trader (GDPR)">⚠</span>`;
            }
            if (row.unsubscribed) {
              nm += ` <span class="tpill tpill--danger" title="Unsubscribed — cannot be added to a drip flow">Unsubscribed</span>`;
            }
            return nm;
          } },
        // NOTE: not in LeadReadService::LIST_FIELDS' sector output — the list
        // endpoint doesn't return per-row sector today (only the single-lead
        // detail fetch does). Real firmographic column available on every row:
        { key: 'company_status', label: 'Company status', plainLabel: 'Company status (Companies House)', width: 150,
          headerFilter: 'list', headerFilterParams: { values: { '': 'Any', active: 'active', dissolved: 'dissolved', liquidation: 'liquidation', administration: 'administration', receivership: 'receivership' }, clearable: true },
          format: (v) => v ? `<span class="tpill tpill--${CSTATUS_TONE[v] || 'neutral'}">${esc(v)}</span>` : '—' },
        { key: 'headline',  label: 'Headline', plainLabel: 'Headline', headerFilterPlaceholder: 'Search…' },
        { key: 'platform',  label: 'Source', plainLabel: 'Source platform', width: 120,
          headerFilter: 'list', headerFilterParams: { values: this.listVals(this.distinct.platform), clearable: true } },
        { key: 'website_framework', label: 'Framework', plainLabel: 'Website framework', width: 120,
          headerFilter: 'list', headerFilterParams: { values: this.listVals(this.distinct.website_framework), clearable: true }, format: (v) => esc(v || '—') },
        { key: 'region',    label: 'Region', plainLabel: 'Region', width: 110,
          headerFilter: 'list', headerFilterParams: { values: this.listVals(this.distinct.region), clearable: true }, format: (v) => esc(v || '—') },
        { key: 'employee_count', label: 'Staff', plainLabel: 'Staff count', width: 84, sorter: 'number', hozAlign: 'center',
          headerFilter: 'number', headerFilterFunc: '>=', headerFilterPlaceholder: '≥',
          format: (v) => (v != null ? esc(v) : '—') },
        { key: 'match_confidence', label: '<span title="Companies House match confidence — name + region + distance.">Match</span>', plainLabel: 'Companies House match confidence', width: 90, sorter: 'number', hozAlign: 'center',
          headerFilter: 'number', headerFilterFunc: '>=', headerFilterPlaceholder: '≥',
          format: (v) => {
            if (v == null) return '—';
            const tone = v >= 75 ? 'success' : (v >= 50 ? 'warning' : 'danger');
            return `<span class="tpill tpill--${tone}" title="Companies House match confidence (name + region + distance)">${esc(v)}</span>`;
          } },
        { key: 'budget_band', label: 'Budget', plainLabel: 'Budget band', width: 120,
          headerFilter: 'list', headerFilterParams: { values: { '': 'Any', minimal: 'minimal', modest: 'modest', moderate: 'moderate', substantial: 'substantial' } },
          format: (v) => v ? `<span class="tpill tpill--${BUDGET_TONE[v] || 'neutral'}">${esc(v)}</span>` : '—' },
        { key: 'current_agency', label: 'Agency', plainLabel: 'Current agency', width: 170, headerFilterPlaceholder: 'Search…',
          format: (v, row) => {
            if (!v && !row.current_agency_url) return '—';
            const name = esc(v || row.current_agency_url);
            // Primary link → the agency's OWN record inside Musubi; else the site; else plain text.
            let html = row.current_agency_lead_id
              ? `<a href="/acquire/prospects/${esc(row.current_agency_lead_id)}">${name}</a>`
              : (row.current_agency_url ? `<a href="${safeUrl(row.current_agency_url)}" target="_blank" rel="noopener">${name}</a>` : name);
            // Secondary link → the agency's external website.
            if (row.current_agency_url) html += ` <a href="${safeUrl(row.current_agency_url)}" target="_blank" rel="noopener" class="small text-muted" title="Agency website">↗</a>`;
            if (row.agency_status === 'dissolved') html += ` <span class="tpill tpill--danger" title="Agency dissolved">×</span>`;
            else if (row.agency_status === 'dead') html += ` <span class="tpill tpill--warning" title="Agency site gone">×</span>`;
            return html;
          } },
        { key: 'pain_points', label: '<span title="Outreach hooks — website / SSL / platform issues found on the prospect.">Hooks</span>', plainLabel: 'Pain-point hooks', width: 200, sorter: false, headerFilter: false,
          format: (v, row) => {
            const ps = row.pain_points || [];
            if (!ps.length) return '<span class="text-muted">—</span>';
            const tone = { high: 'danger', med: 'warning', low: 'neutral' };
            const shown = ps.slice(0, 2).map(p =>
              `<span class="tpill tpill--${tone[p.severity] || 'neutral'} me-1" title="${esc(p.label)}">${esc(p.label)}</span>`).join('');
            return shown + (ps.length > 2 ? `<span class="small text-muted" title="${esc(ps.slice(2).map(p => p.label).join(', '))}">+${ps.length - 2}</span>` : '');
          } },
        { key: 'route',     label: 'Route',  plainLabel: 'Route (none / drip / direct)', width: 100,
          headerFilter: 'list', headerFilterParams: { values: { '': 'All', none: 'none', drip: 'drip', direct: 'direct' }, clearable: true } },
        { key: 'email',     label: 'Email',  plainLabel: 'Email', width: 160, headerFilterPlaceholder: 'Search…',
          format: (v, row) => {
            if (v) return esc(v);
            // No email: show a clear hint, not the raw enrichment_status.
            if (row.enrichment_status) return '<span class="text-muted small">no email</span>';
            return '<span class="text-muted small">not enriched</span>';
          } },
        {
          key: 'id', label: 'Actions', plainLabel: 'Actions', togglable: false, width: 300, headerFilter: false, sorter: false,
          format: (v, row) => {
            const view = `<a class="btn btn-sm btn-link p-0 me-2 lead-open" data-id="${esc(v)}" href="/acquire/prospects/${esc(v)}">View</a>`;
            if (row.stage === 'rejected' || row.stage === 'promoted') {
              return view + `<span class="text-muted small">${esc(row.stage)}</span>`;
            }
            const promote = row.promotable
              ? `<button class="btn btn-sm btn-link p-0 me-2 text-success fw-bold leads-act" data-id="${esc(v)}" data-action="promote">Promote</button>` : '';
            return view + promote
              + `<button class="btn btn-sm btn-link p-0 me-2 leads-act" data-id="${esc(v)}" data-action="enrich">Enrich</button>`
              + `<button class="btn btn-sm btn-link p-0 me-2 leads-act" data-id="${esc(v)}" data-action="qualify">Qualify</button>`
              + `<button class="btn btn-sm btn-link p-0 me-2 leads-act" data-id="${esc(v)}" data-action="route">Route</button>`
              + `<button class="btn btn-sm btn-link p-0 text-danger leads-act" data-id="${esc(v)}" data-action="reject">Reject</button>`;
          },
        },
      ];
    },
    /** Chooser checklist — every real data column except the pinned open-cell + Actions. */
    toggleableColumns() {
      return this.allDataColumns.filter((c) => c.togglable !== false);
    },
    /** What MusubiTabulator actually renders: pinned columns + whatever's checked. */
    columns() {
      return this.allDataColumns.filter((c) => c.togglable === false || this.visibleCols.includes(c.key));
    },
  },
  async mounted() {
    document.addEventListener('click', this.onDelegatedClick);
    // Gate first: a non-entitled tenant sees the Enterprise panel and we never
    // fetch the corpus. The remote table (inside v-else-if) only mounts — and
    // only then auto-fetches its first page — once the gate confirms access.
    await this.checkDataViewsGate();
    if (this.gateLocked) return;
    this.refreshStats();
    this.loadDistinct();
    this.loadOptions();
    this.loadMethods();
    this.applyPipelineParam();
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDelegatedClick);
  },
  methods: {
    // --- F-PT-02: column chooser -------------------------------------------------
    loadVisibleCols() {
      try {
        const raw = localStorage.getItem(VISIBLE_COLS_KEY);
        const arr = raw ? JSON.parse(raw) : null;
        if (Array.isArray(arr) && arr.length) return arr;
      } catch (e) { /* fall through to default */ }
      return [...DEFAULT_VISIBLE_COLS];
    },
    saveVisibleCols() {
      try { localStorage.setItem(VISIBLE_COLS_KEY, JSON.stringify(this.visibleCols)); } catch (e) { /* non-fatal */ }
    },
    toggleColumn(key) {
      const i = this.visibleCols.indexOf(key);
      if (i === -1) this.visibleCols.push(key); else this.visibleCols.splice(i, 1);
      this.saveVisibleCols();
    },
    resetColumns() {
      this.visibleCols = [...DEFAULT_VISIBLE_COLS];
      this.saveVisibleCols();
    },
    closeColumnChooser() { this.columnChooserOpen = false; },
    // --- F-PT-01: open affordance -------------------------------------------------
    /**
     * Capture-phase handler on the table wrapper (see template) — fires before
     * Tabulator's own row-click listener, so clicking the name link or the
     * chevron cell ONLY navigates; it never also flips the row's selection
     * checkbox. Every other click on the row keeps the existing
     * select-on-click behaviour untouched.
     */
    onOpenCapture(ev) {
      const link = ev.target.closest('.lead-open');
      if (!link) return;
      ev.preventDefault();
      ev.stopPropagation();
      const id = link.dataset.id;
      if (id) this.$router.push(`/acquire/prospects/${id}`);
    },
    /**
     * Read /me/usage → the Enterprise `data_views` feature gate. Entitled when the
     * plan enables data_views OR the caller is an operator (plan.can_manage =
     * Ops::PLATFORM). Fail-OPEN on a read error — a usage-endpoint blip should not
     * lock a paying Enterprise tenant out of their corpus browser.
     */
    async checkDataViewsGate() {
      try {
        const res = await this.api.get('/api/v1/me/usage');
        const entitled = !!res?.features?.data_views || !!res?.plan?.can_manage;
        this.gateLocked = !entitled;
      } catch (e) {
        this.gateLocked = false;
      }
    },
    async loadMethods() {
      try {
        const res = await this.api.get('/api/v1/admin/leads/enrich-methods');
        this.methods = (res?.data ?? res)?.data || [];
      } catch (e) { /* non-fatal */ }
    },
    /**
     * ?pipeline={id} deep-link from PipelinesHome's "View matches" — pre-applies
     * the pipeline's FIRST audience source's mapped filters to the visible
     * top-bar controls (stage/temperature/leads-only/sector/company-status),
     * mirroring AudienceFilterMapper's field semantics for those axes, and sets
     * the "Filtered to pipeline" chip. Deliberately partial (honest, not exact):
     *   - only the FIRST source — a multi-source pipeline is an OR-of-sources
     *     (see DripAudienceService), which this AND-only browser can't render;
     *     "Export matches" on the pipeline card is the exact matching set.
     *   - the framework/region/min-staff/min-score axes are Tabulator COLUMN
     *     filters, not top-bar controls, and aren't pre-applied here — they
     *     need the header-filter editors (+ their async distinct-value lists)
     *     already mounted, which races the pipeline fetch. Left for the
     *     operator to add from the table headers if needed.
     * GET /api/v1/admin/drip-flows/{id} needs Ops::OUTREACH — a campaign
     * manager without it just loses the chip/prefill, non-fatally.
     */
    async applyPipelineParam() {
      const pid = Number(new URLSearchParams(window.location.search).get('pipeline') || 0);
      if (!pid) return;
      try {
        const res = await this.api.get(`/api/v1/admin/drip-flows/${pid}`);
        const body = res?.flow !== undefined ? res : (res?.data ?? res);
        const flow = body?.flow;
        if (!flow) return;
        this.pipelineId = pid;
        this.pipelineName = flow.name || `#${pid}`;

        const sources = Array.isArray(flow.audience_json) ? flow.audience_json : [];
        const first = sources[0];
        if (!first || typeof first !== 'object') return;

        if (first.stage) this.stage = String(first.stage);
        if (first.temperature) this.temperature = String(first.temperature);
        if (first.leads_only) this.leadsOnly = true;

        let sectors = first.sectors;
        if (typeof sectors === 'string') sectors = sectors.split(/[,\s]+/).filter(Boolean);
        if (Array.isArray(sectors) && sectors.length) this.sectorTokens = sectors;

        let status = first.company_status;
        if (Array.isArray(status)) status = status[0];
        if (status) this.companyStatus = String(status);

        this.applyTopFilter();
      } catch (e) {
        // Non-fatal — the deep link still loads the unfiltered browser.
      }
    },
    async runBulk() {
      const m = this.methods.find((x) => x.name === this.bulkMethod);
      if (!m) return;
      const scope = [this.temperature && `${this.temperature} leads`, this.stage && `stage "${this.stage}"`].filter(Boolean).join(', ') || 'ALL leads';
      if (!window.confirm(`Run "${m.label}" on ${scope}? It queues in the background (override).`)) return;
      this.bulkBusy = true; this.error = null; this.message = '';
      try {
        const body = {};
        if (this.temperature) body.temperature = this.temperature;
        if (this.stage) body.stage = this.stage;
        const res = await this.api.post(`/api/v1/admin/leads/enrich-methods/${this.bulkMethod}/run-all`, body);
        const p = res?.data ?? res;
        this.message = `Queued ${m.label} for ${p.enqueued} lead(s)${p.capped ? ' (capped at 500 — run again for more)' : ''} — running in the background.`;
      } catch (e) { this.error = e?.response?.data?.error || e?.message || 'Bulk enrich failed.'; }
      this.bulkBusy = false;
    },
    onSelection(rows) {
      // Per-page checkbox ticks. Explicit ticking takes over from a prior
      // "select all matching" so the visible checkboxes are what acts.
      this.selected = Array.isArray(rows) ? rows : [];
      if (this.selected.length) this.allMatching = false;
      this.bulkDripMsg = '';
    },
    /** Top-bar (stage/temp/pain/…) filter change → re-fetch page 1 remotely. */
    applyTopFilter() {
      this.allMatching = false;
      this.allMatchingIds = [];
      this.$refs.leadsTable?.deselect();
      this.$refs.leadsTable?.reload();
      this.refreshStats();
    },
    /** Re-fetch the current page + counts (after an action mutated rows). */
    refresh() {
      this.$refs.leadsTable?.reload();
      this.refreshStats();
    },
    /** Base (top-bar) filter axes shared by the page, stats + ids requests. */
    baseParams() {
      const qs = new URLSearchParams();
      if (this.stage) qs.set('stage', this.stage);
      if (this.temperature) qs.set('temperature', this.temperature);
      if (this.promotable) qs.set('promotable', '1');
      if (this.leadsOnly) qs.set('leads_only', '1');
      if (this.pain) qs.set('pain', this.pain);
      return qs;
    },
    /**
     * The firmographic filters (industry + company status) that aren't Tabulator
     * column headers — folded into the remote-filter array in the same
     * [{field,type,value}] shape the backend already whitelists.
     */
    firmographicFilters() {
      const extra = [];
      if (this.sectorTokens.length) extra.push({ field: 'sector', type: 'in', value: this.sectorTokens });
      if (this.companyStatus) extra.push({ field: 'company_status', type: 'eq', value: this.companyStatus });
      if (this.registrationStatuses.length) extra.push({ field: 'registration_status', type: 'in', value: this.registrationStatuses });
      return extra;
    },
    /** Merge Tabulator column filters with the firmographic filters. */
    mergeFilters(colFilters) {
      return [...(colFilters || []), ...this.firmographicFilters()];
    },
    /** Current per-column header filters (+ firmographic), serialised for stats/ids. */
    columnFilterJson() {
      return JSON.stringify(this.mergeFilters(this.$refs.leadsTable?.columnFilters() || []));
    },
    /** Remote data source for MusubiTabulator — one page per call. */
    async fetcher({ page, size, sort, filters }) {
      const qs = this.baseParams();
      qs.set('page', String(page || 1));
      qs.set('per_page', String(size || 25));
      if (sort?.field) { qs.set('sort', sort.field); qs.set('dir', sort.dir || 'desc'); }
      qs.set('filters', JSON.stringify(this.mergeFilters(filters)));
      try {
        const res = await this.api.get('/api/v1/admin/leads?' + qs.toString());
        // The paged body is { data:[rows], total, … }. useApi returns it raw,
        // so unwrap an axios-style envelope only when there's no `total` here.
        const body = (res && res.total !== undefined) ? res : (res?.data ?? res);
        const rows = body?.data || [];
        this.pageRows = rows; // cache for the route modal lookup
        this.promoteThreshold = body?.promote_threshold ?? this.promoteThreshold;
        return { rows, total: Number(body?.total) || 0 };
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Failed to load prospects.';
        return { rows: [], total: 0 };
      }
    },
    /**
     * Fired by the table on every remote fetch. Refresh the KPI counts + reset a
     * stale "all matching" set when the FILTER set (not just the page) changes.
     */
    onParamsChange({ filters }) {
      const sig = JSON.stringify([
        this.stage, this.temperature, this.pain, this.leadsOnly, this.promotable, filters || [],
        this.sectorTokens, this.companyStatus, this.registrationStatuses,
      ]);
      this.colFiltersActive = Array.isArray(filters) && filters.length > 0;
      if (sig !== this.lastSig) {
        this.lastSig = sig;
        this.allMatching = false;
        this.allMatchingIds = [];
        this.refreshStats();
      }
    },
    async refreshStats() {
      const qs = this.baseParams();
      qs.set('filters', this.columnFilterJson());
      try {
        const res = await this.api.get('/api/v1/admin/leads/stats?' + qs.toString());
        const p = res?.data ?? res;
        if (p?.stats) this.stats = p.stats;
        this.promoteThreshold = p?.promote_threshold ?? this.promoteThreshold;
      } catch (e) { /* KPI cards just stay on the last numbers */ }
    },
    /** Select every prospect matching the current filters (across all pages). */
    async selectAllMatching() {
      this.idsBusy = true; this.bulkDripMsg = '';
      const qs = this.baseParams();
      qs.set('filters', this.columnFilterJson());
      try {
        const res = await this.api.get('/api/v1/admin/leads/ids?' + qs.toString());
        const p = res?.data ?? res;
        this.allMatchingIds = p?.ids || [];
        this.allMatching = true;
        this.$refs.leadsTable?.deselect();
        this.selected = [];
      } catch (e) {
        this.bulkDripMsg = e?.response?.data?.error || e?.message || 'Could not select all matching.';
      }
      this.idsBusy = false;
    },
    clearSelection() {
      this.$refs.leadsTable?.deselect();
      this.selected = [];
      this.allMatching = false;
      this.allMatchingIds = [];
    },
    async addSelectedToDrip() {
      const ids = this.bulkIds;
      if (!this.bulkFlowId || !ids.length) return;
      this.bulkDripBusy = true; this.bulkDripMsg = '';
      try {
        const res = await this.api.post('/api/v1/admin/leads/route-bulk', { ids, drip_flow_id: this.bulkFlowId });
        const p = res?.data ?? res;
        if (p.queued) {
          // Large set — enrolling in the background.
          this.bulkDripMsg = `Queued ${p.total.toLocaleString()} prospects for enrollment — running in the background.`;
        } else {
          const n = p.skipped?.length || 0;
          this.bulkDripMsg = `Added ${p.enrolled} of ${p.total} to the flow${n ? ` — ${n} skipped (unsubscribed or no email)` : ''}.`;
        }
        this.clearSelection();
        this.refresh();
      } catch (e) {
        this.bulkDripMsg = e?.response?.data?.error || e?.message || 'Add to flow failed.';
      }
      this.bulkDripBusy = false;
    },
    async loadDistinct() {
      const fields = ['platform', 'website_framework', 'region'];
      await Promise.all(fields.map(async (f) => {
        try {
          const res = await this.api.get(`/api/v1/admin/leads/distinct/${f}`);
          this.distinct[f] = (res?.data ?? res)?.values || [];
        } catch (e) { /* dropdown falls back to "All" only */ }
      }));
    },
    onDelegatedClick(ev) {
      // Close the column chooser on any click outside it (own toggle button
      // included, since that has its own @click handler already).
      if (this.columnChooserOpen && !ev.target.closest('.col-chooser')) this.columnChooserOpen = false;
      // Close the registration-status dropdown the same way (own toggle button
      // included, since that has its own @click handler already).
      if (this.regStatusOpen && !ev.target.closest('.reg-status-filter')) this.regStatusOpen = false;
      const btn = ev.target.closest('.leads-act');
      if (!btn) return;
      ev.preventDefault();
      ev.stopPropagation();
      const id = Number(btn.dataset.id);
      const action = btn.dataset.action;
      if (!id) return;
      // Inline spinner on the clicked button while the request runs.
      const original = btn.innerHTML;
      btn.innerHTML = cygnusSpriteHtml('spinner', { spin: true });
      btn.style.pointerEvents = 'none';
      const restore = () => { btn.innerHTML = original; btn.style.pointerEvents = ''; };
      let p;
      if (action === 'qualify') p = this.qualify(id);
      else if (action === 'reject') p = this.reject(id);
      else if (action === 'route') { restore(); this.openRoute(id); return; }
      else if (action === 'enrich') p = this.enrich(id);
      else if (action === 'promote') p = this.promote(id);
      Promise.resolve(p).finally(restore);
    },
    async promote(id) { await this.act(`/api/v1/admin/leads/${id}/promote`, `Promoted #${id} → CRM`); },
    async enrich(id) {
      this.error = null; this.message = '';
      try {
        const res = await this.api.post(`/api/v1/admin/leads/${id}/enrich`, {});
        const p = res?.data ?? res;
        const found = Object.values(p.methods || {}).filter((o) => o === 'found').length;
        this.message = found ? `Enriched #${id} — ${found} method(s) returned data.` : `Enriched #${id} — nothing new found.`;
        this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Enrich failed.';
      }
    },
    async qualify(id) { await this.act(`/api/v1/admin/leads/${id}/qualify`, 'Qualified'); },
    async reject(id)  { await this.act(`/api/v1/admin/leads/${id}/reject`, 'Rejected'); },
    async act(url, label) {
      this.error = null; this.message = '';
      try {
        await this.api.post(url, {});
        this.message = `${label}.`;
        this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Action failed.';
      }
    },
    openRoute(id) {
      const lead = this.pageRows.find((r) => r.id === id) || { id, name: '#' + id, platform: '' };
      this.routing = lead;
      this.form = { route: 'drip', email: lead.email || '', list_id: null, drip_flow_id: null, assigned_to: null };
    },
    closeRoute() { this.routing = null; },
    async loadOptions() {
      const unwrap = (r) => {
        const p = r?.data?.data ?? r?.data ?? r;
        return Array.isArray(p) ? p : (p?.rows || []);
      };
      try {
        const [lists, flows] = await Promise.all([
          this.api.get('/api/v1/admin/lists'),
          this.api.get('/api/v1/admin/drip-flows'),
        ]);
        this.lists = unwrap(lists);
        this.flows = unwrap(flows);
      } catch (e) { /* dropdowns stay empty — not fatal */ }
    },
    async submitRoute() {
      this.busy = true; this.error = null;
      const body = { route: this.form.route };
      if (this.form.route === 'drip') {
        body.email = this.form.email;
        if (this.form.list_id) body.list_id = this.form.list_id;
        if (this.form.drip_flow_id) body.drip_flow_id = this.form.drip_flow_id;
      } else if (this.form.assigned_to) {
        body.assigned_to = this.form.assigned_to;
      }
      try {
        const res = await this.api.post(`/api/v1/admin/leads/${this.routing.id}/route`, body);
        const p = res?.data ?? res;
        this.message = `Routed #${this.routing.id} → ${p.route}.`;
        this.routing = null;
        this.refresh();
      } catch (e) {
        this.error = e?.response?.data?.error || e?.message || 'Route failed.';
      }
      this.busy = false;
    },
  },
};
</script>

<style>
.musubi-page--leads { padding: var(--m-space-6, 32px); }

/* Enterprise data-view lock panel (LHS-rework Screen C) — violet plan-gating
   accent, honest "Compare plans", no fabricated corpus numbers. */
.leads-ent-lock {
  max-width: 640px;
  margin: 24px 0;
  padding: 28px 30px;
  background: linear-gradient(135deg, var(--m-violet-soft, #F3EEFE), #FBFAFF);
  border: 1px solid var(--m-violet-line, #E0D2FB);
  border-radius: var(--m-radius-lg, 14px);
  box-shadow: var(--m-shadow-card);
}
.leads-ent-lock__ic {
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 12px;
  background: #fff;
  border: 1px solid var(--m-violet-line, #E0D2FB);
  color: var(--m-violet, #7C3AED);
  font-size: 1.25rem;
  margin-bottom: 14px;
}
.leads-ent-lock__title {
  font-size: 1.1rem; font-weight: 800; letter-spacing: -0.01em;
  color: var(--m-text-primary, #1A1C23);
}
.leads-ent-lock__sub {
  font-size: 0.86rem; color: var(--m-text-secondary, #475569);
  line-height: 1.6; margin: 8px 0 18px;
}
.leads-ent-lock__actions { display: flex; align-items: center; gap: 16px; }
.leads-ent-lock__compare {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.85rem; font-weight: 700;
  color: var(--m-violet, #7C3AED); text-decoration: none;
}
.leads-ent-lock__compare:hover { text-decoration: underline; }
.pipeline-chip {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.82rem; color: var(--m-text-primary, #1A1C23);
  background: var(--m-accent-soft, #EEF0FF); border: 1px solid var(--m-accent, #4F46E5);
  border-radius: var(--m-radius, 8px); padding: 6px 12px; margin-bottom: 12px;
}
.pipeline-chip__clear { margin-left: auto; font-weight: 600; text-decoration: none; }
.lead-kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; margin: 4px 0 18px; }
@media (max-width: 1024px) { .lead-kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 640px)  { .lead-kpis { grid-template-columns: 1fr; } }
.actions-bar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; flex-wrap: wrap; }
.actions-spacer { flex: 1 1 auto; }

/* F-PT-02: column chooser */
.col-chooser { position: relative; }
/* Registration-status filter — reuses .col-chooser__panel / __row for identical
   dropdown styling (see the icons/gotchas section: FE + BE mirror the Direct
   Mail audience filter's registration-status semantics). */
.reg-status-filter { position: relative; }
.col-chooser__panel {
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 60;
  width: 260px; max-height: 340px; overflow-y: auto;
  background: var(--m-bg-card, #fff); border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius-lg, 10px); box-shadow: var(--m-shadow-panel, 0 12px 32px rgba(15,23,42,.14));
  padding: 10px 12px;
}
.col-chooser__head {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--m-text-secondary, #64748B); margin-bottom: 6px; padding-bottom: 6px;
  border-bottom: 1px solid var(--m-border, #E2E8F0);
}
.col-chooser__row {
  display: flex; align-items: center; gap: 8px;
  font-size: 0.82rem; color: var(--m-text-primary, #0F172A);
  padding: 5px 2px; cursor: pointer; user-select: none;
}
.col-chooser__row input { flex: none; }

/* F-PT-01: open affordance — indigo commit accent, consistent with the rest
   of the app's link colour, distinct from the plain-text cell content.
   (This <style> block is intentionally NOT scoped — see .tpill etc. above —
   because Tabulator formatters inject raw innerHTML, bypassing Vue's scoped
   data attributes entirely.) */
.leads-table-wrap .lead-open {
  color: var(--m-accent, #4F46E5);
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
}
.leads-table-wrap .lead-open:hover { text-decoration: underline; }
.leads-table-wrap .lead-open--chevron {
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 6px; font-size: 0.72rem;
  color: var(--m-text-tertiary, #94A3B8);
}
.leads-table-wrap .lead-open--chevron:hover {
  background: var(--m-accent-soft, #EEF0FF); color: var(--m-accent, #4F46E5); text-decoration: none;
}
.actions-bar__msg { font-size: 0.85rem; color: var(--m-text-secondary, #475569); }
.bulk-bar {
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  margin-bottom: 12px; padding: 8px 12px;
  background: var(--m-accent-soft, #EEF0FF); border: 1px solid var(--m-accent, #4F46E5);
  border-radius: var(--m-radius, 8px);
}
.bulk-bar__count { font-size: 0.85rem; color: var(--m-text-primary, #1A1C23); }
.select-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 8px; }
.promotable-toggle { display: inline-flex; align-items: center; gap: 5px; cursor: pointer; user-select: none; }
.leads-modal-overlay { position: fixed; inset: 0; background: var(--m-bg-overlay, rgba(15,23,42,.45)); display: flex; align-items: center; justify-content: center; z-index: 1050; }
.leads-modal {
  background: var(--m-bg-card, #fff);
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius-lg, 14px);
  width: min(460px, 92vw);
  padding: 20px;
  box-shadow: var(--m-shadow-panel, 0 12px 32px rgba(15, 23, 42, .12));
}
.leads-modal__head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; padding-bottom: 12px; border-bottom: 1px solid var(--m-border, #E2E8F0); }
.leads-modal__head h6 { font-weight: 700; }
.leads-modal__foot { display: flex; gap: 8px; margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--m-border, #E2E8F0); }

/* Table-cell status pills (Tabulator formatters emit raw HTML — mirrors MusubiStatusPill's tones) */
.tpill { display: inline-block; font-size: 0.7rem; font-weight: 700; border-radius: 20px; padding: 2px 9px; line-height: 1.5; white-space: nowrap; }
.tpill--success { background: color-mix(in srgb, var(--m-status-success) 16%, var(--m-bg-card, #fff)); color: var(--m-status-success); }
.tpill--warning { background: color-mix(in srgb, var(--m-status-warning) 18%, var(--m-bg-card, #fff)); color: #92600a; }
.tpill--danger  { background: color-mix(in srgb, var(--m-status-danger) 14%, var(--m-bg-card, #fff));  color: var(--m-status-danger); }
.tpill--info    { background: color-mix(in srgb, var(--m-status-info) 14%, var(--m-bg-card, #fff));    color: var(--m-status-info); }
.tpill--accent  { background: var(--m-accent-soft); color: var(--m-accent); }
.tpill--neutral { background: var(--m-bg-content); color: var(--m-text-secondary); border: 1px solid var(--m-border); }
/* Lead temperature reuses the warm(danger)/cold(info) identity tokens from _tokens.scss. */
.tpill--warm { background: color-mix(in srgb, var(--m-status-danger) 12%, var(--m-bg-card, #fff)); color: var(--m-status-danger); }
.tpill--cold { background: color-mix(in srgb, var(--m-status-info) 12%, var(--m-bg-card, #fff));   color: var(--m-status-info); }
</style>
